export type PerspectivePartyType = 'Party 1' | 'Party 2';

export interface EntityAssignmentMetaDataSource {
  confidenceScore: number;
  entityName: string;
  entityType: string;
  genericEntityName: string;
}

export interface ApprovedEntityPartyMapEntry {
  aliases: string[];
  canonicalName: string;
  roleNames: string[];
}

export interface ApprovedEntityAssignmentMetaData {
  documentPerspective: {
    perspectivePartyType: PerspectivePartyType;
  };
  entities: EntityAssignmentMetaDataSource[];
  partyMap: Record<PerspectivePartyType, ApprovedEntityPartyMapEntry>;
}

interface BuildEntityAssignmentMetaDataInput {
  assignments: EntityAssignmentMetaDataSource[];
  perspectivePartyType: PerspectivePartyType;
}

const PARTY_TYPES: PerspectivePartyType[] = ['Party 1', 'Party 2'];

const normalizeWhitespace = (value: string) => value.trim().replace(/\s+/g, ' ');

const normalizeComparison = (value: string) =>
  normalizeWhitespace(value).toLowerCase();

const uniqueStrings = (values: string[]) => {
  const seen = new Set<string>();

  return values.filter((value) => {
    const normalizedValue = normalizeComparison(value);

    if (!normalizedValue || seen.has(normalizedValue)) {
      return false;
    }

    seen.add(normalizedValue);
    return true;
  });
};

const normalizeCasedWord = (value: string) => {
  const alphaOnly = value.replace(/[^A-Za-z]/g, '');

  if (!alphaOnly) {
    return value;
  }

  if (alphaOnly.length <= 3 && value === value.toUpperCase()) {
    return value;
  }

  return value
    .split(/(['’\-])/)
    .map((part) => {
      if (part === "'" || part === '’' || part === '-') {
        return part;
      }

      if (!part) {
        return part;
      }

      const lettersOnly = part.replace(/[^A-Za-z]/g, '');

      if (lettersOnly.length <= 3 && part === part.toUpperCase()) {
        return part;
      }

      return `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`;
    })
    .join('');
};

const formatDisplayName = (value: string) => {
  const trimmedValue = normalizeWhitespace(value);

  if (!trimmedValue) {
    return '';
  }

  const hasLetters = /[A-Za-z]/.test(trimmedValue);
  const hasLowercaseLetters = /[a-z]/.test(trimmedValue);

  if (!hasLetters || hasLowercaseLetters) {
    return trimmedValue;
  }

  return trimmedValue
    .split(/\s+/)
    .map(normalizeCasedWord)
    .join(' ');
};

const isDirectRoleAlias = (entityName: string, genericEntityName: string) => {
  const normalizedEntityName = normalizeComparison(entityName);
  const normalizedGenericEntityName = normalizeComparison(genericEntityName);

  if (!normalizedEntityName || !normalizedGenericEntityName) {
    return false;
  }

  return (
    normalizedEntityName === normalizedGenericEntityName ||
    normalizedEntityName === `the ${normalizedGenericEntityName}`
  );
};

const isPossessiveDescriptor = (entityName: string) =>
  /['’]s\s+/i.test(normalizeWhitespace(entityName));

const startsWithArticle = (entityName: string) =>
  normalizeComparison(entityName).startsWith('the ');

const rankCanonicalCandidate = (entity: EntityAssignmentMetaDataSource) => {
  let score = entity.confidenceScore * 100;

  if (!isDirectRoleAlias(entity.entityName, entity.genericEntityName)) {
    score += 25;
  }

  if (!isPossessiveDescriptor(entity.entityName)) {
    score += 15;
  }

  if (!startsWithArticle(entity.entityName)) {
    score += 10;
  }

  if (/\s/.test(normalizeWhitespace(entity.entityName))) {
    score += 5;
  }

  return score;
};

const getPrimaryRoleName = (assignments: EntityAssignmentMetaDataSource[]) => {
  const roleNameMap = new Map<
    string,
    { count: number; displayName: string; maxConfidenceScore: number }
  >();

  assignments.forEach(({ confidenceScore, genericEntityName }) => {
    const normalizedGenericEntityName = normalizeComparison(genericEntityName);

    if (!normalizedGenericEntityName) {
      return;
    }

    const currentEntry = roleNameMap.get(normalizedGenericEntityName);

    if (!currentEntry) {
      roleNameMap.set(normalizedGenericEntityName, {
        count: 1,
        displayName: formatDisplayName(genericEntityName),
        maxConfidenceScore: confidenceScore
      });
      return;
    }

    roleNameMap.set(normalizedGenericEntityName, {
      count: currentEntry.count + 1,
      displayName: currentEntry.displayName,
      maxConfidenceScore: Math.max(
        currentEntry.maxConfidenceScore,
        confidenceScore
      )
    });
  });

  const sortedRoleEntries = [...roleNameMap.values()].sort((left, right) => {
    if (right.count !== left.count) {
      return right.count - left.count;
    }

    if (right.maxConfidenceScore !== left.maxConfidenceScore) {
      return right.maxConfidenceScore - left.maxConfidenceScore;
    }

    return left.displayName.localeCompare(right.displayName);
  });

  return sortedRoleEntries[0]?.displayName ?? '';
};

const buildPartyMapEntry = (
  assignments: EntityAssignmentMetaDataSource[]
): ApprovedEntityPartyMapEntry => {
  if (assignments.length === 0) {
    return {
      aliases: [],
      canonicalName: '',
      roleNames: []
    };
  }

  const preferredCanonicalCandidates = assignments.filter(
    (assignment) =>
      !isDirectRoleAlias(assignment.entityName, assignment.genericEntityName) &&
      !isPossessiveDescriptor(assignment.entityName) &&
      !startsWithArticle(assignment.entityName)
  );

  const canonicalSource = (
    preferredCanonicalCandidates.length > 0
      ? preferredCanonicalCandidates
      : assignments
  )
    .slice()
    .sort((left, right) => rankCanonicalCandidate(right) - rankCanonicalCandidate(left))[0];

  const explicitRoleNames = uniqueStrings(
    assignments
      .filter((assignment) =>
        isDirectRoleAlias(assignment.entityName, assignment.genericEntityName)
      )
      .map((assignment) => formatDisplayName(assignment.genericEntityName))
  );

  const fallbackRoleName = getPrimaryRoleName(assignments);
  const roleNames =
    explicitRoleNames.length > 0
      ? explicitRoleNames
      : fallbackRoleName
        ? [fallbackRoleName]
        : [];

  const canonicalName = formatDisplayName(
    canonicalSource?.entityName || roleNames[0] || ''
  );

  return {
    aliases: uniqueStrings([canonicalName, ...roleNames]),
    canonicalName,
    roleNames
  };
};

const normalizeEntityType = (entityType: string) => {
  const normalizedEntityType = normalizeComparison(entityType);

  if (normalizedEntityType === 'party 1') {
    return 'Party 1';
  }

  if (normalizedEntityType === 'party 2') {
    return 'Party 2';
  }

  if (normalizedEntityType === 'ignore') {
    return 'Ignore';
  }

  if (normalizedEntityType === 'unknown') {
    return 'Unknown';
  }

  return normalizeWhitespace(entityType);
};

export const buildEntityAssignmentMetaData = ({
  assignments,
  perspectivePartyType
}: BuildEntityAssignmentMetaDataInput): ApprovedEntityAssignmentMetaData => {
  const partyMap = PARTY_TYPES.reduce<
    Record<PerspectivePartyType, ApprovedEntityPartyMapEntry>
  >((nextPartyMap, partyType) => {
    nextPartyMap[partyType] = buildPartyMapEntry(
      assignments.filter(
        (assignment) =>
          normalizeComparison(assignment.entityType) ===
          normalizeComparison(partyType)
      )
    );

    return nextPartyMap;
  }, {} as Record<PerspectivePartyType, ApprovedEntityPartyMapEntry>);

  const payloadEntities = assignments
    .filter((assignment) => {
      const normalizedEntityType = normalizeComparison(assignment.entityType);

      return normalizedEntityType !== 'ignore';
    })
    .map(
      ({
        confidenceScore,
        entityName,
        entityType,
        genericEntityName
      }): EntityAssignmentMetaDataSource => ({
        confidenceScore,
        entityName: formatDisplayName(entityName),
        entityType: normalizeEntityType(entityType),
        genericEntityName: formatDisplayName(genericEntityName)
      })
    );

  return {
    documentPerspective: {
      perspectivePartyType
    },
    entities: payloadEntities,
    partyMap
  };
};
